import type { Room } from 'colyseus.js';
import { NET } from '../config/tuning';

/**
 * Client side of the clock-sync handshake — design/gdd/movement-netcode.md
 * Core Rule #6 / Formula 3, extended with a third `clock.ack` leg (see the
 * amendment note in that GDD): the original ping/pong pair let the CLIENT
 * compute its own offset, but the SERVER also needs it to translate incoming
 * input timestamps, which requires the client to report its own receipt
 * time back. This class does not need the offset itself — the server owns
 * that — it just runs the ping loop and estimates local RTT for the debug HUD.
 */
export class ClientClockSync {
  private rttSmoothed: number | null = null;
  private burstRemaining = NET.CLOCK_PING_BURST_COUNT;
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(private readonly room: Room) {
    this.room.onMessage('clock.pong', (msg: { clientTs: number; serverTs: number }) => {
      const clientReceivedAt = performance.now();
      this.room.send('clock.ack', { clientTs: msg.clientTs, serverTs: msg.serverTs, clientReceivedAt });

      const rttSample = clientReceivedAt - msg.clientTs;
      if (this.rttSmoothed === null) {
        this.rttSmoothed = rttSample;
      } else {
        this.rttSmoothed = this.rttSmoothed * (1 - NET.CLOCK_EWMA_ALPHA) + rttSample * NET.CLOCK_EWMA_ALPHA;
      }
    });
  }

  start(): void {
    this.stopped = false;
    this.scheduleNext();
  }

  stop(): void {
    this.stopped = true;
    if (this.timeoutHandle) clearTimeout(this.timeoutHandle);
  }

  get rtt(): number {
    return this.rttSmoothed ?? 0;
  }

  private scheduleNext(): void {
    this.sendPing();
    if (this.stopped) return;
    const delay = this.burstRemaining > 0 ? NET.CLOCK_PING_BURST_INTERVAL_MS : NET.CLOCK_PING_INTERVAL_MS;
    this.timeoutHandle = setTimeout(() => this.scheduleNext(), delay);
  }

  private sendPing(): void {
    if (this.burstRemaining > 0) this.burstRemaining -= 1;
    this.room.send('clock.ping', { clientTs: performance.now() });
  }
}
